import { useState } from 'react';
import { motion } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ScrambleText } from '@/components/ScrambleText';
import { ArrowRight, Instagram } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CODE_LINE_3 = 'submit(name,email,role)=>validate()&&store(db.ticket_requests)=>respond({status:"received"});//await_ticket';

export const ROLE_OPTIONS = [
  { value: 'Collector', label: 'Collector' },
  { value: 'Artist', label: 'Artist' },
  { value: 'Press', label: 'Press' },
  { value: 'Curator / Gallery', label: 'Curator / Gallery' },
  { value: 'Friend of the Gallery', label: 'Friend of the Gallery' },
];

export default function FooterForm() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error('Please enter your name.'); return; }
    if (!email.trim()) { toast.error('Please enter your email.'); return; }
    if (!role) { toast.error('Please tell us who you are.'); return; }

    setLoading(true);
    try {
      await axios.post(`${API}/ticket-request`, {
        name: name.trim(),
        email: email.trim(),
        role,
      });
      setDone(true);
      toast.success('Your ticket is on its way — check your email.');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Something went wrong. Try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="ticket-form"
      data-testid="footer-form-section"
      className="relative z-10 px-6 sm:px-10 py-20 lg:py-28"
    >
      <div className="overflow-hidden mb-16">
        <div className="code-divider-scroll whitespace-nowrap text-[10px] font-mono text-[#222222] select-none">
          {CODE_LINE_3} {CODE_LINE_3} {CODE_LINE_3} {CODE_LINE_3}
        </div>
      </div>

      <div className="max-w-3xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-60px' }}
          transition={{ duration: 0.6 }}
        >
          <ScrambleText
            text="REQUEST YOUR TICKET"
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight uppercase text-white font-outfit block"
            as="h2"
            duration={800}
          />

          <p className="text-xs sm:text-sm font-mono text-[#f6f6f6] mt-4 mb-12 tracking-wide">
            Tell us a little about you and we&apos;ll send your ticket by email.
          </p>

          {done ? (
            <motion.div
              data-testid="submission-success"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="py-8"
            >
              <p className="text-white font-mono text-sm tracking-[0.15em] uppercase">
                YOUR TICKET IS ON ITS WAY
              </p>
              <p className="text-[#f6f6f6] font-mono text-xs mt-3 tracking-wide">
                Check your inbox — we just emailed your ticket from The Relative Gallery.
              </p>
            </motion.div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid="ticket-form"
              className="max-w-xl mx-auto space-y-3 text-left"
            >
              <Input
                data-testid="name-input"
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full bg-transparent border-[#333333] text-white rounded-none font-mono text-sm placeholder:text-[#444444] focus-visible:ring-white focus-visible:ring-1 h-12"
              />
              <Input
                data-testid="email-input"
                type="email"
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="w-full bg-transparent border-[#333333] text-white rounded-none font-mono text-sm placeholder:text-[#444444] focus-visible:ring-white focus-visible:ring-1 h-12"
              />
              <Select value={role} onValueChange={setRole}>
                <SelectTrigger
                  data-testid="role-select"
                  className="w-full bg-transparent border-[#333333] text-white rounded-none font-mono text-sm h-12 focus:ring-white"
                >
                  <SelectValue placeholder="Who are you?" />
                </SelectTrigger>
                <SelectContent className="bg-[#0a0a0a] border-[#333333] rounded-none">
                  {ROLE_OPTIONS.map((p) => (
                    <SelectItem
                      key={p.value}
                      value={p.value}
                      data-testid={`role-${p.value.replace(/\s|\//g, '-').toLowerCase()}`}
                      className="font-mono text-sm text-[#cccccc] focus:bg-[#1a1a1a] focus:text-white rounded-none"
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                data-testid="submit-ticket-button"
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-[#cccccc] rounded-none uppercase tracking-[0.15em] text-[11px] font-mono font-medium h-12 px-7 gap-2"
              >
                {loading ? 'SUBMITTING...' : 'GET MY TICKET'}
                {!loading && <ArrowRight className="w-3 h-3" />}
              </Button>
            </form>
          )}
        </motion.div>

        <div className="mt-24 pt-8 border-t border-[#1a1a1a]">
          <a
            href="https://www.instagram.com/the_relative_gallery/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="inline-flex items-center gap-2 text-[#f6f6f6] hover:text-white transition-colors duration-300 mb-4"
            style={{ cursor: 'pointer' }}
          >
            <Instagram className="w-4 h-4" />
            <span className="text-[10px] font-mono tracking-[0.15em] uppercase">@the_relative_gallery</span>
          </a>
          <p className="text-[10px] font-mono text-[#f6f6f6] tracking-[0.15em] uppercase">
            Hosted by Mr. Relative &times; Odesso
          </p>
          <p className="text-[10px] font-mono text-[#f6f6f6] tracking-[0.15em] mt-2">
            &copy; 2026 The Relative Gallery. All rights reserved.
          </p>
        </div>
      </div>
    </section>
  );
}
